import { useState, useEffect, useCallback, useMemo } from 'react'
import { Plus, X, ChevronRight, ExternalLink, CheckSquare, Square, Mail, AlertCircle } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { format } from 'date-fns'
import { computeAllClientAlerts, ALERT_STYLES } from './alerts'
import TaskModal from './TaskModal'
import TaskComments from './TaskComments'
import { TaskTimerButton } from './TaskTimer'
import { isActiveProfile, DONE_STATUSES, CLIENT_APPROVED_STATUSES, BLOG_PUBLISH_WARNING } from './constants'
import { BLOG_FLOW, sendRevisionToWorker, reopenReviewAfterResubmit } from './workflowRevisions'

const BLOG_PHASES = [
  { value: 'drafting', label: 'Writing', icon: '✍️', color: '#2a6e84', bg: '#e8f5fa' },
  { value: 'review', label: 'AD Review', icon: '👁', color: '#8c4d78', bg: '#f9edf5' },
  { value: 'client_review', label: 'Client Review', icon: '⏳', color: '#c4962a', bg: '#fff8e1' },
  { value: 'publishing', label: 'Publishing', icon: '🚀', color: '#2e6e3a', bg: '#e8f5eb' },
  { value: 'complete', label: 'Complete', icon: '✅', color: '#2e6e3a', bg: '#e8f5eb' },
]
const getPhase = v => BLOG_PHASES.find(p => p.value === v)

// Reference docs shown on every blog detail
const BLOG_REFERENCE_DOCS = [
  { label: '2026 Blog Structural Guidelines (AEO)', url: 'https://docs.google.com/document/d/1nUz0EH3GZi-tmvfH200eiK2GICKlm_FE7o5CjQ_xHfo/edit?usp=sharing' },
]

const CHECKLIST_1 = [
  { id: 'h1', label: 'H1 is a natural-language question, under 70 characters, with primary keyword up front' },
  { id: 'author', label: 'Author block, credentials, and one-line bio appear under the H1 with a link to the author page' },
  { id: 'first_para', label: 'First paragraph directly answers the H1 question' },
  { id: 'tldr', label: 'TL;DR / Key Takeaways section is under 100 words and placed before the first H2' },
  { id: 'toc', label: 'Table of Contents lists every H2 with matching anchor text' },
  { id: 'h2_questions', label: 'Every H2 (and most H3s) is phrased as a question' },
  { id: 'chunk_answers', label: "Each chunk's first sentence directly answers the heading" },
  { id: 'paragraphs', label: 'Paragraphs are 2–4 sentences. No walls of text.' },
  { id: 'taco_bell', label: 'Every chunk passes the Taco Bell Test (self-contained, on-topic, recognizable as the client)' },
  { id: 'faq', label: 'FAQ has 3–6 distinct questions with 2–4 sentence answers' },
  { id: 'about', label: 'Entity-rich "About [Practice]" paragraph closes the post' },
  { id: 'external_links', label: 'All external claims link to primary sources (APA, NIH, peer-reviewed)' },
  { id: 'internal_links', label: 'All required internal links from the brief are included' },
  { id: 'voice_search', label: 'Voice search check: short sentences, clear subjects, no ambiguous pronouns' },
  { id: 'aeo_audit', label: 'AI audit completed (paste into Claude/ChatGPT and ask the four AEO questions)' },
  { id: 'not_ai', label: 'Not written by AI. Original voice, original insight, original phrasing.' },
  { id: 'word_count', label: 'Target length: ~1,500 words' },
]

const CHECKLIST_2 = [
  { id: 'faq_format', label: 'FAQ is formatted with H3 headings and built in the Easy Accordion plugin' },
  { id: 'anchors', label: 'Create anchor links from the table of contents to the chunks they are associated with' },
  { id: 'seo_title', label: 'SEO title is written (55–60 characters, includes primary keyword)' },
  { id: 'meta', label: 'Meta description is written (150–160 characters, previews answer-first paragraph, includes primary keyword)' },
  { id: 'slug', label: 'URL slug is clean, descriptive, and keyword-rich' },
  { id: 'image', label: 'Featured image uploaded with descriptive alt text' },
  { id: 'updated_stamp', label: '"Updated: MM/DD/YY" stamp is visible as plain on-page text' },
  { id: 'author_schema', label: "Author schema or byline links to the author's full bio page" },
  { id: 'internal_live', label: 'Internal links from the brief are live and pointing to correct URLs' },
  { id: 'external_live', label: 'External citations are live and pointing to primary sources' },
  { id: 'newsletter', label: "Post is added to client's newsletter queue (if they have newsletters)" },
  { id: 'gsc_index', label: 'Submit the URL to Google Search Console for indexing' },
]

// ─── Standalone field component (outside modal to prevent focus loss) ──
function BlogField({ label, k, placeholder, rows, hint, type, form, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{hint}</div>}
      {rows
        ? <textarea className="form-control" rows={rows} value={form[k] || ''} onChange={e => onChange(k, e.target.value)} placeholder={placeholder} />
        : <input className="form-control" type={type || 'text'} value={form[k] || ''} onChange={e => onChange(k, e.target.value)} placeholder={placeholder} />
      }
    </div>
  )
}

// ─── Reference docs row ─────────────────────────────────────────────────────
function ReferenceDocsRow() {
  return (
    <div style={{ background: 'var(--cream)', borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
        📚 Reference docs (open for guidelines)
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {BLOG_REFERENCE_DOCS.map(doc => (
          <a key={doc.url} href={doc.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
            <ExternalLink size={12} /> {doc.label}
          </a>
        ))}
      </div>
    </div>
  )
}

// ─── New Blog Assignment Form ─────────────────────────────────────────────────
function NewBlogModal({ clients, profiles, currentUser, draft, onClose, onSaved }) {
  const toast = useToast()
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  // If resuming a saved draft, remember its id so we update (not duplicate) it.
  const [draftId, setDraftId] = useState(draft?.id || null)
  const FORM_FIELDS = [
    'client_id', 'assigned_to', 'assistant_am_id',
    'draft_due_date', 'target_publish_date', 'client_email',
    'persona', 'emotional_state',
    'author_name', 'author_one_liner', 'author_bio_url',
    'working_h1', 'primary_keyword', 'secondary_keywords',
    'answer_first', 'proposed_h2s', 'proposed_faqs',
    'required_internal_links', 'required_external_citations', 'client_quotes',
    'primary_cta', 'cta_url', 'about_paragraph',
    'tone', 'words_to_avoid', 'words_to_use', 'sensitive_topics', 'required_disclaimers',
    'additional_notes',
  ]
  const [form, setForm] = useState(() => {
    const base = Object.fromEntries(FORM_FIELDS.map(k => [k, '']))
    if (draft) {
      for (const k of FORM_FIELDS) {
        if (draft[k] !== null && draft[k] !== undefined) base[k] = draft[k]
      }
    }
    return base
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const ams = profiles

  // Build the payload of brief fields (shared by draft + final save)
  const briefPayload = () => ({
    ...Object.fromEntries(FORM_FIELDS.map(k => [k, form[k] === '' ? null : form[k]])),
    draft_due_date: form.draft_due_date || null,
    target_publish_date: form.target_publish_date || null,
    assistant_am_id: form.assistant_am_id || null,
    assigned_to: form.assigned_to || null,
    client_id: form.client_id || null,
    assigned_by: currentUser.id,
  })

  // Save as a draft (status='draft') WITHOUT spawning the 4 phase tasks.
  // Lets the user close and come back to finish the brief later.
  const saveDraft = async () => {
    if (!form.client_id && !form.working_h1) {
      toast('Add at least a client or a title before saving a draft', 'error'); return
    }
    setSavingDraft(true)
    try {
      const payload = { ...briefPayload(), status: 'draft' }
      if (draftId) {
        const { error } = await supabase.from('blog_assignments').update(payload).eq('id', draftId)
        if (error) throw error
      } else {
        const { data, error } = await supabase.from('blog_assignments').insert(payload).select().single()
        if (error) throw error
        setDraftId(data.id)
      }
      toast('Draft saved — you can close and finish it later', 'success')
      onSaved()
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSavingDraft(false)
    }
  }

  const save = async () => {
    if (!form.client_id) { toast('Select a client', 'error'); return }
    if (!form.assigned_to) { toast('Select a copywriter', 'error'); return }
    if (!form.working_h1) { toast('Add the H1/title', 'error'); return }
    setSaving(true)
    try {
      // Create OR finalize the blog assignment. If resuming a draft, update
      // that row and flip it to 'drafting'; otherwise insert a fresh row.
      let blog
      if (draftId) {
        const { data, error } = await supabase.from('blog_assignments')
          .update({ ...briefPayload(), status: 'drafting' })
          .eq('id', draftId)
          .select().single()
        if (error) throw error
        blog = data
      } else {
        const { data, error } = await supabase.from('blog_assignments').insert({
          ...briefPayload(),
          status: 'drafting',
        }).select().single()
        if (error) throw error
        blog = data
      }

      const client = clients.find(c => c.id === form.client_id)
      const clientName = client?.name || 'Client'

      // Create the 4 phase tasks
      const assignedBy = currentUser.id
      const tasks = [
        {
          title: `✍️ Write & Draft: ${form.working_h1}`,
          client_id: form.client_id,
          assigned_to: form.assigned_to,
          assigned_by: assignedBy,
          date_due: form.draft_due_date || null,
          status: 'not_started',
          service_area: 'copywriting',
          approval_path: 'internal',
          blog_assignment_id: blog.id,
          blog_phase: 'draft',
          client_visible: false,
          collaborator_ids: [],
          deliverable_links: [],
          google_doc_links: [],
          loom_links: [],
          checklist_items: CHECKLIST_1.map(i => ({ ...i, checked: false })),
          description: `Blog brief: ${form.working_h1}\nPrimary keyword: ${form.primary_keyword}\nPersona: ${form.persona}`,
        },
        {
          title: `👁 Review Blog: ${form.working_h1}`,
          client_id: form.client_id,
          assigned_to: assignedBy,
          assigned_by: assignedBy,
          date_due: form.draft_due_date || null,
          status: 'not_started',
          service_area: 'copywriting',
          approval_path: 'internal_then_client_fyi',
          blog_assignment_id: blog.id,
          blog_phase: 'review',
          client_visible: false,
          collaborator_ids: [],
          deliverable_links: [],
          google_doc_links: [],
          loom_links: [],
          checklist_items: [],
          description: 'Review the copywriter draft and approve or request revisions before sending to client.',
        },
        {
          title: `📨 Send to Client: ${form.working_h1}`,
          client_id: form.client_id,
          assigned_to: assignedBy,
          assigned_by: assignedBy,
          date_due: form.target_publish_date || null,
          status: 'not_started',
          service_area: 'copywriting',
          approval_path: 'internal_client_live',
          blog_assignment_id: blog.id,
          blog_phase: 'client_review',
          client_visible: false,
          collaborator_ids: [],
          deliverable_links: [],
          google_doc_links: [],
          loom_links: [],
          checklist_items: [],
          description: `Send blog to ${clientName} for approval. Client email: ${form.client_email || 'not set'}`,
          notes: `Client email: ${form.client_email || 'check client profile'}`,
        },
        {
          title: `🚀 Publish Checklist: ${form.working_h1}`,
          client_id: form.client_id,
          assigned_to: form.assistant_am_id || assignedBy,
          assigned_by: assignedBy,
          date_due: form.target_publish_date || null,
          status: 'not_started',
          service_area: 'copywriting',
          approval_path: 'internal',
          blog_assignment_id: blog.id,
          blog_phase: 'publish_checklist',
          client_visible: false,
          collaborator_ids: [],
          deliverable_links: [],
          google_doc_links: [],
          loom_links: [],
          checklist_items: CHECKLIST_2.map(i => ({ ...i, checked: false })),
          description: 'Complete the technical and SEO publish checklist after client approval.',
        },
      ]

      const { data: createdTasks, error: tasksError } = await supabase.from('tasks').insert(tasks).select()
      if (tasksError) throw tasksError

      // Notify copywriter
      await supabase.from('notifications').insert({
        user_id: form.assigned_to,
        task_id: createdTasks?.[0]?.id,
        type: 'comment',
        message: `New blog assignment: "${form.working_h1}" for ${clientName}`,
      })

      toast('Blog assignment created! 4 tasks generated.', 'success')
      onSaved()
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const STEPS = ['Basics', 'Audience', 'Author', 'Topic', 'Structure', 'CTA', 'Voice', 'Notes']

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">New Blog Assignment</div>
            <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
              {STEPS.map((s, i) => (
                <button key={i} onClick={() => setStep(i + 1)}
                  style={{ padding: '3px 10px', borderRadius: 50, fontSize: 11, border: 'none', cursor: 'pointer', fontWeight: step === i + 1 ? 700 : 400, background: step === i + 1 ? 'var(--navy)' : 'var(--cream-dark)', color: step === i + 1 ? '#fff' : 'var(--text-muted)' }}>
                  {i + 1}. {s}
                </button>
              ))}
            </div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body">
          {step === 1 && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>1. Assignment Basics</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Client *</label>
                  <select className="form-control" value={form.client_id} onChange={e => set('client_id', e.target.value)}>
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Client Email</label>
                  <input className="form-control" value={form.client_email} onChange={e => set('client_email', e.target.value)} placeholder="client@practice.com" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Copywriter (Assigned To) *</label>
                  <select className="form-control" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                    <option value="">Select copywriter...</option>
                    {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Optimization &amp; Publish (Assigned To)</label>
                  <select className="form-control" value={form.assistant_am_id} onChange={e => set('assistant_am_id', e.target.value)}>
                    <option value="">Select person...</option>
                    {ams.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <BlogField label="Draft Due Date" k="draft_due_date" type="date" form={form} onChange={set} />
                <BlogField label="Target Publish Date" k="target_publish_date" type="date" form={form} onChange={set} />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>2. Audience & Persona</div>
              <BlogField label="Persona + Intent Stage" k="persona" placeholder='e.g. "High-performing professional with anxiety in Chicago, Awareness stage"' hint="From the client's AEO grid. Combine persona and funnel stage." form={form} onChange={set} />
              <BlogField label="What They're Feeling" k="emotional_state" rows={2} placeholder="The emotional state when they search this. Pull from real client language if possible." form={form} onChange={set} />
            </>
          )}

          {step === 3 && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>3. Author</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Every practice needs 2-3 authors we can ghostwrite for. Alternative: "By the Clinical Team at [Practice]"</div>
              <BlogField label="Author + Credentials" k="author_name" placeholder='e.g. "Dr. Sarah Chen, LCPC"' form={form} onChange={set} />
              <BlogField label="Author One-Liner" k="author_one_liner" placeholder="One-sentence specialty blurb to display under the H1" form={form} onChange={set} />
              <BlogField label="Author Bio Page URL" k="author_bio_url" placeholder="https://..." form={form} onChange={set} />
            </>
          )}

          {step === 4 && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>4. Topic & Search</div>
              <BlogField label="Working H1 / Title *" k="working_h1" placeholder="Phrase as a natural-language question. Under 70 characters. Front-load the primary keyword." form={form} onChange={set} />
              <BlogField label="Primary Keyword" k="primary_keyword" placeholder="The main search phrase we want this blog to rank for" form={form} onChange={set} />
              <BlogField label="Secondary Keywords" k="secondary_keywords" rows={2} placeholder="2-4 related phrases the copywriter should weave in naturally (one per line)" form={form} onChange={set} />
            </>
          )}

          {step === 5 && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>5. Content Structure</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Target length: ~1,500 words for every LMR blog.</div>
              <BlogField label="The Answer-First Answer" k="answer_first" rows={3} placeholder="The 2-3 sentence direct answer to the H1 that opens the post." form={form} onChange={set} />
              <BlogField label="Proposed H2s (Chunks)" k="proposed_h2s" rows={4} placeholder="List 4-7 H2 sections in order, one per line. Phrase as questions when possible." form={form} onChange={set} />
              <BlogField label="Proposed FAQ Questions" k="proposed_faqs" rows={3} placeholder="3-6 questions for the FAQ. Different from H2s — edge cases and objections." form={form} onChange={set} />
              <BlogField label="Required Internal Links" k="required_internal_links" rows={2} placeholder="Service pages, related blogs, author pages, contact page. List URLs." form={form} onChange={set} />
              <BlogField label="Required External Citations" k="required_external_citations" rows={2} placeholder="Primary sources (APA, NIH, peer-reviewed journals). List URLs." form={form} onChange={set} />
              <BlogField label="Client Quotes" k="client_quotes" rows={3} placeholder="2-3 direct quotes from the practice owner or clinician. Paste here for the copywriter to work in." form={form} onChange={set} />
            </>
          )}

          {step === 6 && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>6. Call to Action & About Block</div>
              <BlogField label="Primary CTA" k="primary_cta" placeholder="What's the one action we want the reader to take?" form={form} onChange={set} />
              <BlogField label="CTA Destination URL" k="cta_url" placeholder="https://..." form={form} onChange={set} />
              <BlogField label="Entity-Rich About Paragraph" k="about_paragraph" rows={4} placeholder='Draft the 2-3 sentence "About [Practice]" block that closes the post. Include modalities, populations served, location, and what makes the practice different.' form={form} onChange={set} />
            </>
          )}

          {step === 7 && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>7. Brand Voice & Sensitivity</div>
              <BlogField label="Tone" k="tone" placeholder="Warm and conversational? Clinical and authoritative? Faith-informed?" form={form} onChange={set} />
              <BlogField label="Words / Phrases to Avoid" k="words_to_avoid" rows={2} placeholder='e.g. "patient" instead of "client", avoid "struggle" or "suffer"' form={form} onChange={set} />
              <BlogField label="Words / Phrases to Use" k="words_to_use" rows={2} placeholder="Preferred terminology, modality names, client-approved language" form={form} onChange={set} />
              <BlogField label="Sensitive Topics in This Post" k="sensitive_topics" rows={2} placeholder="Suicide, self-harm, eating disorders, abuse, substance use — flag if this post touches these" form={form} onChange={set} />
              <BlogField label="Required Disclaimers" k="required_disclaimers" rows={2} placeholder="Medication, diagnosis, crisis resources — list any required disclaimers" form={form} onChange={set} />
            </>
          )}

          {step === 8 && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>8. Additional Notes & Context</div>
              <BlogField label="Additional Notes" k="additional_notes" rows={6} placeholder="Anything else the copywriter needs to know — recent client feedback, competitor posts to avoid duplicating, related posts in the pipeline, specific examples to include, etc." form={form} onChange={set} />
            </>
          )}
        </div>

        <div className="modal-footer">
          <div style={{ marginRight: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>Step {step} of {STEPS.length}</div>
          <button className="btn btn-ghost" onClick={saveDraft} disabled={savingDraft || saving}>
            {savingDraft ? 'Saving…' : (draftId ? 'Update draft' : 'Save draft')}
          </button>
          {step > 1 && <button className="btn btn-ghost" onClick={() => setStep(s => s - 1)}>← Back</button>}
          {step < STEPS.length
            ? <button className="btn btn-gold" onClick={() => setStep(s => s + 1)}>Next →</button>
            : <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Creating...' : '🚀 Create Blog Assignment'}</button>
          }
        </div>
      </div>
    </div>
  )
}

// Safe date formatter
function safeFmt(dateStr, fmt) {
  if (!dateStr) return null
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T12:00:00')
    if (isNaN(d.getTime())) return null
    return format(d, fmt)
  } catch { return null }
}

// ─── Blog Phase Task Detail ─────────────────────────────────────────────────
export function BlogPhaseTask({ task, blog, profiles, clients, currentUser, onUpdated, onOpenInPanel, focused }) {
  const toast = useToast()
  const [checklist, setChecklist] = useState(task.checklist_items || [])
  const [saving, setSaving] = useState(false)
  const [newDocLink, setNewDocLink] = useState('')
  const [localLinks, setLocalLinks] = useState(task.deliverable_links || [])
  const [showRevisionInput, setShowRevisionInput] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [revisionNote, setRevisionNote] = useState('')
  const [localStatus, setLocalStatus] = useState(task.status)

  // Stay in sync if task prop changes
  useEffect(() => {
    setChecklist(task.checklist_items || [])
    setLocalLinks(task.deliverable_links || [])
    setLocalStatus(task.status)
  }, [task.id, task.status, task.checklist_items, task.deliverable_links])

  // Step 4 only: look up the step 3 (client_review) task so we can warn the
  // publisher until the client has approved. Re-checks whenever this task's
  // status changes (client approval flips it to 'goes_live').
  const [clientApproved, setClientApproved] = useState(null) // null = unknown / not applicable
  useEffect(() => {
    if (task.blog_phase !== 'publish_checklist' || !task.blog_assignment_id) { setClientApproved(null); return }
    let cancelled = false
    ;(async () => {
      const { data, error } = await supabase.from('tasks').select('status')
        .eq('blog_assignment_id', task.blog_assignment_id)
        .eq('blog_phase', 'client_review')
        .maybeSingle()
      if (cancelled) return
      setClientApproved(error || !data ? null : CLIENT_APPROVED_STATUSES.includes(data.status))
    })()
    return () => { cancelled = true }
  }, [task.id, task.blog_phase, task.blog_assignment_id, task.status])
  const showClientWarning = task.blog_phase === 'publish_checklist' && clientApproved === false && !DONE_STATUSES.includes(localStatus)

  const allChecked = checklist.length > 0 && checklist.every(i => i.checked)
  const checkedCount = checklist.filter(i => i.checked).length
  const isAssignee = task.assigned_to === currentUser.id
  const isAssigner = task.assigned_by === currentUser.id

  const updateStatus = async (newStatus, opts = {}) => {
    setSaving(true)
    await supabase.from('tasks').update({ status: newStatus }).eq('id', task.id)
    setLocalStatus(newStatus)

    // Resubmitting after revisions → put the AD's review step back to Ready for Review
    if (task.blog_phase === 'draft' && newStatus === 'ready_for_review') {
      await reopenReviewAfterResubmit(BLOG_FLOW, task)
    }

    // Update parent blog status based on phase + new task status.
    // We advance the blog_assignment status to reflect the active phase at all
    // meaningful checkpoints — not just when a phase fully completes.
    if (task.blog_assignment_id && task.blog_phase) {
      let nextBlogStatus = null

      if (task.blog_phase === 'draft') {
        // Copywriter submitted draft → show "AD Review" on the blog card
        if (['ready_for_review', 'approved', 'live'].includes(newStatus)) {
          nextBlogStatus = 'review'
        }
      } else if (task.blog_phase === 'review') {
        // AD approved internally or sent to client → show "Client Review"
        if (['approved', 'live', 'internally_approved', 'awaiting_client'].includes(newStatus)) {
          nextBlogStatus = 'client_review'
          // Also mark the sibling draft task approved so it doesn't strand
          await supabase.from('tasks').update({ status: 'approved' })
            .eq('blog_assignment_id', task.blog_assignment_id).eq('blog_phase', 'draft')
        }
      } else if (task.blog_phase === 'client_review') {
        // Client approved → show "Publishing"
        if (['approved', 'live', 'client_approved'].includes(newStatus)) {
          nextBlogStatus = 'publishing'
          // Hand off to the publisher: flip their publish-checklist task from the
          // dormant 'not_started' to 'goes_live' ("Ready to Deploy") so it visibly
          // changes state on their board and lands in the deploy queue.
          // Only flip if not started, so a publisher who already started isn't knocked back.
          await supabase.from('tasks').update({ status: 'goes_live' })
            .eq('blog_assignment_id', task.blog_assignment_id)
            .eq('blog_phase', 'publish_checklist')
            .eq('status', 'not_started')
        }
      } else if (task.blog_phase === 'publish_checklist') {
        if (['approved', 'live'].includes(newStatus)) {
          nextBlogStatus = 'complete'
        }
      }

      if (nextBlogStatus) {
        await supabase.from('blog_assignments').update({ status: nextBlogStatus }).eq('id', task.blog_assignment_id)
      }
    }

    // Notify
    const notifyId = newStatus === 'ready_for_review' ? task.assigned_by : task.assigned_to
    if (notifyId && notifyId !== currentUser.id) {
      const msgs = {
        ready_for_review: `"${task.title}" is ready for review`,
        approved: `"${task.title}" has been approved`,
        needs_revision: `"${task.title}" needs revisions`,
        in_progress: `"${task.title}" has been started`,
      }
      await supabase.from('notifications').insert({ user_id: notifyId, task_id: task.id, type: newStatus, message: msgs[newStatus] || `"${task.title}" status updated` })
    }

    if (!opts.silent) toast(newStatus === 'ready_for_review' ? 'Submitted for review!' : newStatus === 'approved' ? 'Approved!' : newStatus === 'needs_revision' ? 'Sent back for revisions' : 'Status updated', 'success')
    onUpdated()
    setSaving(false)
  }

  // Review step (AD) or client step: send the work back to the copywriter so it
  // lands in THEIR tasks as Needs Revision. Client revisions also reset this
  // client step to Not Started and re-open the AD's review step.
  const submitRevision = async () => {
    const note = revisionNote.trim()
    if (!note) { toast('Please explain what needs to be revised', 'error'); return }
    setSaving(true)
    const isClient = task.blog_phase === 'client_review'
    await supabase.from('task_comments').insert({ task_id: task.id, author_id: currentUser.id, content: `${isClient ? '↩ Client requested revisions' : '↩ Revision requested'}: ${note}`, mentions: [] })
    const { error } = await sendRevisionToWorker(BLOG_FLOW, task, note, currentUser.id, isClient ? 'client' : 'internal')
    if (error) {
      toast('Could not send back to the copywriter: ' + error.message, 'error')
      setSaving(false)
      return
    }
    await updateStatus(isClient ? 'not_started' : 'needs_revision', { silent: true })
    toast('Sent back to the copywriter for revisions', 'success')
    setShowRevisionInput(false)
    setRevisionNote('')
    setSaving(false)
  }

  const toggleItem = async (id) => {
    const updated = checklist.map(i => i.id === id ? { ...i, checked: !i.checked } : i)
    setChecklist(updated)
    await supabase.from('tasks').update({ checklist_items: updated }).eq('id', task.id)
  }

  const addDocLink = async () => {
    if (!newDocLink.trim()) return
    const updated = [...localLinks, newDocLink.trim()]
    setLocalLinks(updated)
    setNewDocLink('')
    await supabase.from('tasks').update({ deliverable_links: updated }).eq('id', task.id)
    toast('Blog doc link added!', 'success')
  }

  const sendToClient = () => {
    const docLink = localLinks[0] || task.deliverable_links?.[0] || ''
    const clientName = clients.find(c => c.id === task.client_id)?.name || 'your practice'
    const subject = encodeURIComponent(`Blog Draft Ready for Your Review — ${blog?.working_h1 || task.title}`)
    const body = encodeURIComponent(
      `Hi,\n\nYour blog draft is ready for review! This post has been written and optimized for both AEO (Answer Engine Optimization) and SEO to help your practice show up in AI-powered search results.\n\n` +
      `📄 Blog Draft: ${docLink}\n\n` +
      `Please review and let us know:\n• ✅ Approved as-is\n• ✏️ Revisions needed (please add comments directly in the Google Doc)\n\n` +
      `We're excited about this one — it's well-positioned to rank for "${blog?.primary_keyword || 'your target keyword'}".\n\nBest,\n${profiles.find(p => p.id === currentUser.id)?.full_name || 'LMR Team'}`
    )
    window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(blog?.client_email || '')}&su=${subject}&body=${body}`, '_blank')
  }

  const statusColors = {
    not_started: { bg: 'var(--cream-dark)', color: 'var(--text-muted)' },
    goes_live: { bg: '#f9edf5', color: '#8c4d78' },
    in_progress: { bg: '#e8f5fa', color: '#2a6e84' },
    ready_for_review: { bg: '#fff8e1', color: '#8a6000' },
    needs_revision: { bg: '#fff8e1', color: '#c4962a' },
    approved: { bg: '#e8f5eb', color: '#2e6e3a' },
    live: { bg: '#e8f5eb', color: '#2e6e3a' },
  }
  const sc = statusColors[localStatus] || statusColors.not_started

  return (
    <div style={{ background: '#fff', border: '1px solid var(--border-light)', borderRadius: 14, padding: 18, marginBottom: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{task.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {profiles.find(p => p.id === task.assigned_to)?.full_name || 'Unassigned'}
            {task.date_due && !isNaN(new Date(task.date_due)) && ` · Due ${safeFmt(task.date_due, 'MMM d')}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          <button onClick={() => setShowEdit(true)} title="Edit task (dates, assignee, collaborators)"
            style={{ background: 'var(--cream)', border: '1px solid var(--border-light)', color: 'var(--text-secondary)', padding: '4px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            ✎ Edit
          </button>
          {onOpenInPanel && (
            <button onClick={onOpenInPanel} title="Open in side panel"
              style={{ background: 'var(--cream)', border: '1px solid var(--border-light)', color: 'var(--text-secondary)', padding: '4px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              ⇲ Panel
            </button>
          )}
          <span style={{ fontSize: 11, padding: '3px 12px', borderRadius: 50, background: sc.bg, color: sc.color, fontWeight: 600 }}>
            {localStatus.replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      {/* Step 4 warning: client hasn't approved step 3 yet (warn only, buttons still work) */}
      {showClientWarning && (
        <div role="alert" style={{ marginBottom: 12, padding: '12px 14px', background: 'var(--red)', color: '#fff', borderRadius: 10, fontSize: 13, fontWeight: 700, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{BLOG_PUBLISH_WARNING}</span>
        </div>
      )}

      {/* Brief reminder for copywriter on draft phase */}
      {task.blog_phase === 'draft' && blog?.working_h1 && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: 'var(--navy)', borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>Your Assignment</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: '#fff', marginBottom: 4 }}>{blog.working_h1}</div>
          {blog.primary_keyword && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>🔑 Primary keyword: {blog.primary_keyword}</div>}
          {blog.persona && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>👤 Persona: {blog.persona}</div>}
        </div>
      )}

      {/* Blog doc link — always show */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
          📄 Blog Google Doc
        </div>
        {localLinks.map((link, i) => (
          <a key={i} href={link} target="_blank" rel="noreferrer" className="link-tag" style={{ background: '#e8f5eb', color: '#2e6e3a', marginBottom: 4, display: 'inline-flex' }}>
            <ExternalLink size={11} /><span>Open Blog Draft</span>
          </a>
        ))}
        {localLinks.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No doc linked yet</span>}
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input value={newDocLink} onChange={e => setNewDocLink(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addDocLink()}
            placeholder="Paste Google Doc link..."
            style={{ flex: 1, padding: '6px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-body)', outline: 'none' }} />
          <button onClick={addDocLink} style={{ padding: '6px 12px', background: '#e8f5eb', color: '#2e6e3a', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>Add</button>
        </div>
      </div>

      {/* Action buttons — logic based on phase and status */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: checklist.length > 0 ? 12 : 0 }}>

        {/* DRAFT PHASE: copywriter starts and submits */}
        {task.blog_phase === 'draft' && localStatus === 'not_started' && (
          <button className="btn btn-sm" style={{ background: 'var(--lightblue)', color: 'var(--navy)', border: 'none' }}
            onClick={() => updateStatus('in_progress')} disabled={saving}>▶ Start Writing</button>
        )}
        {task.blog_phase === 'draft' && localStatus === 'in_progress' && (
          <button className="btn btn-sm btn-gold" onClick={() => {
            if (checklist.length > 0 && !allChecked) { toast(`Complete all ${checklist.length} checklist items first (${checkedCount} done)`, 'error'); return }
            updateStatus('ready_for_review')
          }} disabled={saving}>
            ✓ Submit Draft for Review {checklist.length > 0 && `(${checkedCount}/${checklist.length})`}
          </button>
        )}
        {task.blog_phase === 'draft' && localStatus === 'needs_revision' && (
          <button className="btn btn-sm" style={{ background: '#fff8e1', color: '#8a6000', border: '1.5px solid #f0d98a' }}
            onClick={() => updateStatus('in_progress')} disabled={saving}>↩ Start Revision</button>
        )}

        {/* REVIEW PHASE: AD sees approve/revise immediately — no submit needed */}
        {task.blog_phase === 'review' && (localStatus === 'not_started' || localStatus === 'in_progress' || localStatus === 'ready_for_review') && !showRevisionInput && (
          <>
            <button className="btn btn-sm" style={{ background: '#e8f5eb', color: '#2e6e3a', border: 'none' }}
              onClick={() => updateStatus('approved')} disabled={saving}>✅ Approve — Send to Client</button>
            <button className="btn btn-sm" style={{ background: '#fff8e1', color: '#8a6000', border: '1.5px solid #f0d98a' }}
              onClick={() => setShowRevisionInput(true)}>↩ Request Revisions</button>
          </>
        )}
        {task.blog_phase === 'review' && localStatus === 'needs_revision' && (
          <span style={{ fontSize: 12, color: '#8a6000', fontStyle: 'italic' }}>Revisions requested — waiting for copywriter</span>
        )}

        {/* CLIENT REVIEW PHASE */}
        {task.blog_phase === 'client_review' && localStatus === 'not_started' && (
          <button className="btn btn-sm" style={{ background: 'var(--lightblue)', color: 'var(--navy)', border: 'none' }}
            onClick={() => updateStatus('in_progress')} disabled={saving}>▶ Start</button>
        )}
        {task.blog_phase === 'client_review' && (localStatus === 'in_progress' || localStatus === 'not_started') && (
          <button className="btn btn-sm" style={{ background: '#e8f5fa', color: '#2a6e84', border: 'none', display: 'flex', alignItems: 'center', gap: 5 }}
            onClick={sendToClient}><Mail size={13} /> Open Gmail to Send</button>
        )}
        {task.blog_phase === 'client_review' && localStatus === 'in_progress' && !showRevisionInput && (
          <>
            <button className="btn btn-sm" style={{ background: '#e8f5eb', color: '#2e6e3a', border: 'none' }}
              onClick={() => updateStatus('approved')} disabled={saving}>✅ Client Approved</button>
            <button className="btn btn-sm" style={{ background: '#fff8e1', color: '#8a6000', border: '1.5px solid #f0d98a' }}
              onClick={() => setShowRevisionInput(true)}>↩ Client Requested Revisions</button>
          </>
        )}

        {/* PUBLISH CHECKLIST PHASE */}
        {task.blog_phase === 'publish_checklist' && ['not_started', 'goes_live'].includes(localStatus) && (
          <button className="btn btn-sm" style={{ background: 'var(--lightblue)', color: 'var(--navy)', border: 'none' }}
            onClick={() => updateStatus('in_progress')} disabled={saving}>▶ Start Publish Checklist</button>
        )}
        {task.blog_phase === 'publish_checklist' && localStatus === 'in_progress' && (
          <button className="btn btn-sm" style={{ background: '#e8f5eb', color: '#2e6e3a', border: 'none' }}
            onClick={() => {
              if (checklist.length > 0 && !allChecked) { toast(`Complete all ${checklist.length} checklist items first`, 'error'); return }
              updateStatus('live')
            }} disabled={saving}>
            ✅ Complete & Publish {checklist.length > 0 && `(${checkedCount}/${checklist.length})`}
          </button>
        )}

        {/* Reset */}
        {localStatus !== 'not_started' && (
          <button className="btn btn-sm btn-ghost" style={{ fontSize: 11, marginLeft: 'auto' }}
            onClick={() => { if (window.confirm('Reset this phase back to Not Started?')) updateStatus('not_started') }} disabled={saving}>
            ↺ Reset
          </button>
        )}
      </div>

      {/* Revision input */}
      {showRevisionInput && (
        <div style={{ marginBottom: 12, padding: 12, background: '#fff8e1', borderRadius: 10, border: '1px solid #f0d98a' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#8a6000', marginBottom: 8 }}>What needs to be revised?</div>
          <textarea value={revisionNote} onChange={e => setRevisionNote(e.target.value)}
            placeholder="Be specific about what needs to change..."
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #f0d98a', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', resize: 'none', outline: 'none', minHeight: 70, marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm" style={{ background: '#c4962a', color: '#fff', border: 'none' }}
              onClick={submitRevision} disabled={saving || !revisionNote.trim()}>
              Send Back for Revisions
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => { setShowRevisionInput(false); setRevisionNote('') }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Checklist */}
      {checklist.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
            Checklist — {checkedCount}/{checklist.length} {allChecked && '✅'}
          </div>
          <div style={{ background: 'var(--cream)', borderRadius: 10, padding: '10px 12px', maxHeight: 300, overflowY: 'auto' }}>
            {checklist.map(item => (
              <label key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={item.checked} onChange={() => toggleItem(item.id)} style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{ color: item.checked ? 'var(--text-muted)' : 'var(--text-primary)', textDecoration: item.checked ? 'line-through' : 'none' }}>
                  {item.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {focused && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>⏱ Time tracking</div>
          <TaskTimerButton task={task} />
        </div>
      )}

      {focused && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>💬 Comments</div>
          <TaskComments taskId={task.id} taskTitle={task.title} profiles={profiles} currentUser={currentUser} assignedBy={task.assigned_by} assignedTo={task.assigned_to} />
        </div>
      )}

      {showEdit && (
        <TaskModal task={task} clients={clients} profiles={profiles} currentUser={currentUser}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); onUpdated && onUpdated() }} />
      )}
    </div>
  )
}

// ─── Side panel for a single blog task ──────────────────────────────────────
export function BlogPhaseSidePanel({ task, blog, allBlogTasks, profiles, clients, currentUser, onSwitchTask, onClose, onUpdated, onOpenFullWorkflow }) {
  const phaseOrder = { draft: 0, review: 1, client_review: 2, publish_checklist: 3 }
  const phaseLabels = {
    draft:             { icon: '✍️', label: 'Write' },
    review:            { icon: '👁',  label: 'Review' },
    client_review:     { icon: '📨', label: 'Client' },
    publish_checklist: { icon: '🚀', label: 'Publish' },
  }
  const sortedTasks = (allBlogTasks || []).slice().sort((a, b) => (phaseOrder[a.blog_phase] ?? 9) - (phaseOrder[b.blog_phase] ?? 9))

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,43,74,0.3)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 580, background: '#fff', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header — fixed at top */}
        <div style={{ background: 'var(--navy)', padding: '18px 22px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Blog · {blog?.working_h1?.slice(0, 50)}{blog?.working_h1?.length > 50 ? '...' : ''}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {onOpenFullWorkflow && (
              <button onClick={onOpenFullWorkflow} title="View full workflow"
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '5px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>
                📋 Full Workflow
              </button>
            )}
            <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff' }} onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        {/* Phase stepper — fixed below header */}
        {sortedTasks.length > 0 && (
          <div style={{ display: 'flex', gap: 4, padding: '10px 16px', background: 'var(--cream)', borderBottom: '1px solid var(--border-light)', overflowX: 'auto', flexShrink: 0 }}>
            {sortedTasks.map(t => {
              const p = phaseLabels[t.blog_phase] || { icon: '•', label: t.blog_phase }
              const isActive = t.id === task.id
              const isDone = ['approved', 'live'].includes(t.status)
              return (
                <button key={t.id} onClick={() => onSwitchTask && onSwitchTask(t)}
                  style={{
                    flex: 1, padding: '6px 8px', borderRadius: 8, fontSize: 11, border: 'none', cursor: 'pointer',
                    fontWeight: isActive ? 700 : 500,
                    background: isActive ? 'var(--navy)' : (isDone ? '#e8f5eb' : 'transparent'),
                    color: isActive ? '#fff' : (isDone ? '#2e6e3a' : 'var(--text-muted)'),
                    minWidth: 90, whiteSpace: 'nowrap',
                  }}>
                  {isDone ? '✓ ' : `${p.icon} `}{p.label}
                </button>
              )
            })}
          </div>
        )}

        {/* Scrollable body */}
        <div style={{ padding: '18px 20px', flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <ReferenceDocsRow />
          <BlogPhaseTask task={task} blog={blog} profiles={profiles} clients={clients} currentUser={currentUser} onUpdated={onUpdated} focused />
        </div>
      </div>
    </div>
  )
}

// ─── Blog Detail View (full workflow page) ──────────────────────────────────
function BlogDetail({ blog, clients, profiles, currentUser, onBack, onUpdated, onOpenSidePanel }) {
  const [tasks, setTasks] = useState([])
  const [activeSection, setActiveSection] = useState('workflow')

  const load = useCallback(async () => {
    const { data } = await supabase.from('tasks').select('*').eq('blog_assignment_id', blog.id).order('created_at')
    const phaseOrder = { draft: 0, review: 1, client_review: 2, publish_checklist: 3 }
    setTasks((data || []).sort((a, b) => (phaseOrder[a.blog_phase] ?? 9) - (phaseOrder[b.blog_phase] ?? 9)))
  }, [blog.id])

  useEffect(() => { load() }, [load])

  const client = clients.find(c => c.id === blog.client_id)
  const copywriter = profiles.find(p => p.id === blog.assigned_to)
  const phase = getPhase(blog.status)

  const sections = ['workflow', 'brief', 'structure', 'voice']
  const sectionLabels = { workflow: '📋 Workflow', brief: '📝 Brief', structure: '🏗 Structure', voice: '🎙 Voice & CTA' }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back</button>
        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', marginLeft: 'auto' }}
          onClick={async () => {
            if (!window.confirm('Delete this blog assignment and all 4 associated tasks? This cannot be undone.')) return
            await supabase.from('tasks').delete().eq('blog_assignment_id', blog.id)
            await supabase.from('blog_assignments').delete().eq('id', blog.id)
            onBack()
          }}>
          🗑 Delete Assignment
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--navy)' }}>{blog.working_h1}</h1>
            {phase && <span className="status-pill" style={{ background: phase.bg, color: phase.color }}>{phase.icon} {phase.label}</span>}
          </div>
          <div style={{ display: 'flex', gap: 10, fontSize: 13, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
            {client && <span>📍 {client.name}</span>}
            {copywriter && <span>✍️ {copywriter.full_name}</span>}
            {blog.primary_keyword && <span>🔑 {blog.primary_keyword}</span>}
            {blog.draft_due_date && !isNaN(new Date(blog.draft_due_date)) && <span>📅 Draft due {safeFmt(blog.draft_due_date, 'MMM d')}</span>}
          </div>
        </div>
      </div>

      {/* Section tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {sections.map(s => (
          <button key={s} className={`tab ${activeSection === s ? 'active' : ''}`} onClick={() => setActiveSection(s)}>
            {sectionLabels[s]}
          </button>
        ))}
      </div>

      {activeSection === 'workflow' && (
        <div>
          <ReferenceDocsRow />
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Each phase must be completed in order. The copywriter starts, then the AD reviews, then the client approves, then the AAM publishes.
          </div>
          {tasks.map(task => (
            <BlogPhaseTask
              key={task.id}
              task={task}
              blog={blog}
              profiles={profiles}
              clients={clients}
              currentUser={currentUser}
              onUpdated={load}
              onOpenInPanel={onOpenSidePanel ? () => onOpenSidePanel(task, tasks) : null}
            />
          ))}
        </div>
      )}

      {activeSection === 'brief' && (
        <div className="card card-body">
          {blog.working_h1 && (
            <div style={{ marginBottom: 20, padding: '14px 16px', background: 'var(--navy)', borderRadius: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>H1 / Title</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: '#fff', lineHeight: 1.4 }}>{blog.working_h1}</div>
              {blog.primary_keyword && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>🔑 {blog.primary_keyword}</div>}
            </div>
          )}
          {[
            { label: 'Persona + Intent Stage', value: blog.persona },
            { label: "What They're Feeling", value: blog.emotional_state },
            { label: 'Author + Credentials', value: blog.author_name },
            { label: 'Author One-Liner', value: blog.author_one_liner },
            { label: 'Author Bio URL', value: blog.author_bio_url, link: true },
            { label: 'Primary Keyword', value: blog.primary_keyword },
            { label: 'Secondary Keywords', value: blog.secondary_keywords },
            { label: 'Client Quotes', value: blog.client_quotes },
          ].map(f => f.value ? (
            <div key={f.label} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{f.label}</div>
              {f.link ? <a href={f.value} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', fontSize: 13 }}>{f.value}</a>
                : <p style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{f.value}</p>}
            </div>
          ) : null)}
        </div>
      )}

      {activeSection === 'structure' && (
        <div className="card card-body">
          {[
            { label: 'The Answer-First Answer', value: blog.answer_first },
            { label: 'Proposed H2s (Chunks)', value: blog.proposed_h2s },
            { label: 'Proposed FAQ Questions', value: blog.proposed_faqs },
            { label: 'Required Internal Links', value: blog.required_internal_links },
            { label: 'Required External Citations', value: blog.required_external_citations },
            { label: 'Additional Notes', value: blog.additional_notes },
          ].map(f => f.value ? (
            <div key={f.label} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{f.label}</div>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{f.value}</p>
            </div>
          ) : null)}
        </div>
      )}

      {activeSection === 'voice' && (
        <div className="card card-body">
          {[
            { label: 'Tone', value: blog.tone },
            { label: 'Words / Phrases to Avoid', value: blog.words_to_avoid },
            { label: 'Words / Phrases to Use', value: blog.words_to_use },
            { label: 'Sensitive Topics', value: blog.sensitive_topics },
            { label: 'Required Disclaimers', value: blog.required_disclaimers },
            { label: 'Primary CTA', value: blog.primary_cta },
            { label: 'CTA URL', value: blog.cta_url, link: true },
            { label: 'About Paragraph', value: blog.about_paragraph },
          ].map(f => f.value ? (
            <div key={f.label} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{f.label}</div>
              {f.link ? <a href={f.value} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', fontSize: 13 }}>{f.value}</a>
                : <p style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{f.value}</p>}
            </div>
          ) : null)}
        </div>
      )}
    </div>
  )
}

// ─── Reusable alert banner ─────────────────────────────────────────────────
function ClientAlertsBanner({ title, subtitle, items, onOpenClientProfile }) {
  const [expanded, setExpanded] = useState(items.length <= 3)
  const shown = expanded ? items : items.slice(0, 3)
  return (
    <div style={{ background: '#fff8e1', border: '1.5px solid #f0d98a', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: shown.length > 0 ? 10 : 0 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#8a6000', display: 'flex', alignItems: 'center', gap: 6 }}>
            ⏰ {title}
          </div>
          <div style={{ fontSize: 12, color: '#8a6000', opacity: 0.85, marginTop: 2 }}>{subtitle}</div>
        </div>
        {items.length > 3 && (
          <button onClick={() => setExpanded(e => !e)}
            style={{ background: 'transparent', border: 'none', color: '#8a6000', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
            {expanded ? 'Show less' : `Show all ${items.length}`}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {shown.map(({ client, alert }) => (
          <button key={client.id}
            onClick={() => onOpenClientProfile && onOpenClientProfile(client.id)}
            style={{
              background: '#fff', border: '1px solid #f0d98a', color: '#8a6000',
              padding: '5px 10px', borderRadius: 50, fontSize: 12, cursor: 'pointer',
              fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5,
            }}>
            {client.name}
            <ChevronRight size={11} />
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Blog Assignments List ────────────────────────────────────────────────────
export default function BlogAssignmentsView({ clients, profiles, currentUser, profile, selectedBlogId, selectedBlogTaskId, onClearBlogId, onOpenClientProfile }) {
  const [blogs, setBlogs] = useState([])
  const [allBatches, setAllBatches] = useState([])
  const [selected, setSelected] = useState(null)
  const [sidePanelTask, setSidePanelTask] = useState(null)
  const [sidePanelBlog, setSidePanelBlog] = useState(null)
  const [sidePanelAllTasks, setSidePanelAllTasks] = useState([])
  const [showNew, setShowNew] = useState(false)
  const [editingDraft, setEditingDraft] = useState(null)
  const [filterClient, setFilterClient] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [loading, setLoading] = useState(true)

  const canCreate = ['supervising_ad', 'ad', 'am'].includes(profile?.role)

  const load = useCallback(async () => {
    setLoading(true)
    let q = supabase.from('blog_assignments').select('*, client:clients(name), copywriter:profiles!blog_assignments_assigned_to_fkey(full_name)').order('created_at', { ascending: false })
    if (filterClient) q = q.eq('client_id', filterClient)
    if (filterStatus) q = q.eq('status', filterStatus)
    const { data: rawBlogs } = await q

    // Fetch all phase tasks for the loaded blogs so we can correct stale statuses
    const blogIds = (rawBlogs || []).map(b => b.id)
    let tasksByBlog = {}
    if (blogIds.length > 0) {
      const { data: phaseTasks } = await supabase.from('tasks').select('id, blog_assignment_id, blog_phase, status').in('blog_assignment_id', blogIds)
      for (const t of (phaseTasks || [])) {
        if (!tasksByBlog[t.blog_assignment_id]) tasksByBlog[t.blog_assignment_id] = []
        tasksByBlog[t.blog_assignment_id].push(t)
      }
    }

    // For each blog, find the most active task and attach its status for display.
    // "Most active" = the furthest-along phase that isn't fully done yet.
    const phaseOrder = { draft: 0, review: 1, client_review: 2, publish_checklist: 3 }
    const doneStatuses = ['approved', 'live']

    const correctedBlogs = (rawBlogs || []).map(blog => {
      if (blog.status === 'draft' || blog.status === 'complete') return blog
      const tasks = tasksByBlog[blog.id] || []
      if (tasks.length === 0) return blog

      const sorted = tasks.slice().sort((a, b) => (phaseOrder[a.blog_phase] ?? 9) - (phaseOrder[b.blog_phase] ?? 9))

      // Pick the most active task: prefer tasks with a real status over not_started,
      // and among those pick the furthest along in phase order.
      const notDone = sorted.filter(t => !doneStatuses.includes(t.status))
      const withActivity = notDone.filter(t => t.status !== 'not_started')
      // For tasks with activity: furthest along. For not_started fallback: first task (so new blogs show Writing, not Publishing)
      const active = withActivity.slice(-1)[0] || notDone[0] || sorted.slice(-1)[0]
      return { ...blog, _activeTaskStatus: active?.status, _activeTaskPhase: active?.blog_phase }
    })

    setBlogs(correctedBlogs)
    // Also pull all blogs (unfiltered) and batches for alert computation
    const { data: allBlogsForAlerts } = await supabase.from('blog_assignments').select('id, client_id, status, updated_at, created_at, working_h1')
    const { data: batchesData } = await supabase.from('social_batches').select('id, client_id, status, updated_at, created_at')
    setAllBatches(batchesData || [])
    // Store all blogs in a ref-like state for alert computation
    setAllBlogsForAlerts(allBlogsForAlerts || [])
    setLoading(false)
  }, [filterClient, filterStatus])

  const [allBlogsForAlerts, setAllBlogsForAlerts] = useState([])

  // Compute clients needing attention (blog cadence alert only — this is the blogs view)
  const clientsNeedingBlogs = useMemo(() => {
    const alertMap = computeAllClientAlerts(clients, allBlogsForAlerts, allBatches)
    const result = []
    for (const [clientId, alerts] of alertMap.entries()) {
      const blogAlert = alerts.find(a => a.type === 'blog_cadence')
      if (blogAlert) {
        const client = clients.find(c => c.id === clientId)
        if (client) result.push({ client, alert: blogAlert })
      }
    }
    return result
  }, [clients, allBlogsForAlerts, allBatches])

  useEffect(() => { load() }, [load])

  // When navigated from a task click: open the side panel for that specific task
  useEffect(() => {
    if (!selectedBlogId) return
    ;(async () => {
      const { data: foundBlog } = await supabase.from('blog_assignments').select('*, client:clients(name), copywriter:profiles!blog_assignments_assigned_to_fkey(full_name)').eq('id', selectedBlogId).maybeSingle()
      if (!foundBlog) { onClearBlogId && onClearBlogId(); return }

      const { data: phaseTasks } = await supabase.from('tasks').select('*').eq('blog_assignment_id', foundBlog.id).order('created_at')
      const tasks = phaseTasks || []

      let target = null
      if (selectedBlogTaskId) {
        target = tasks.find(t => t.id === selectedBlogTaskId)
      }
      if (!target) {
        target = tasks.find(t => t.assigned_to === currentUser.id && !['approved', 'live'].includes(t.status))
      }
      if (!target) {
        target = tasks.find(t => !['approved', 'live'].includes(t.status)) || tasks[0]
      }

      if (target) {
        setSidePanelTask(target)
        setSidePanelBlog(foundBlog)
        setSidePanelAllTasks(tasks)
      }
      onClearBlogId && onClearBlogId()
    })()
  }, [selectedBlogId, selectedBlogTaskId, currentUser.id, onClearBlogId])

  const reloadSidePanel = async () => {
    if (!sidePanelTask) return
    const { data: phaseTasks } = await supabase.from('tasks').select('*').eq('blog_assignment_id', sidePanelBlog.id).order('created_at')
    setSidePanelAllTasks(phaseTasks || [])
    const refreshed = (phaseTasks || []).find(t => t.id === sidePanelTask.id)
    if (refreshed) setSidePanelTask(refreshed)
    const { data: b } = await supabase.from('blog_assignments').select('*').eq('id', sidePanelBlog.id).single()
    if (b) setSidePanelBlog(b)
    load()
  }

  const openFullWorkflowFromPanel = () => {
    if (!sidePanelBlog) return
    setSelected(sidePanelBlog)
    setSidePanelTask(null)
    setSidePanelBlog(null)
    setSidePanelAllTasks([])
  }

  if (selected) {
    return (
      <>
        <BlogDetail blog={selected} clients={clients} profiles={profiles} currentUser={currentUser}
          onBack={() => { setSelected(null); load() }}
          onUpdated={load}
          onOpenSidePanel={(task, allTasks) => { setSidePanelTask(task); setSidePanelBlog(selected); setSidePanelAllTasks(allTasks) }}
        />
        {sidePanelTask && sidePanelBlog && (
          <BlogPhaseSidePanel
            task={sidePanelTask}
            blog={sidePanelBlog}
            allBlogTasks={sidePanelAllTasks}
            profiles={profiles}
            clients={clients}
            currentUser={currentUser}
            onSwitchTask={t => setSidePanelTask(t)}
            onClose={() => { setSidePanelTask(null); setSidePanelBlog(null); setSidePanelAllTasks([]); load() }}
            onUpdated={reloadSidePanel}
            onOpenFullWorkflow={null}
          />
        )}
      </>
    )
  }

  return (
    <div>
      {clientsNeedingBlogs.length > 0 && (
        <ClientAlertsBanner
          title="Clients needing a new blog"
          subtitle={`${clientsNeedingBlogs.length} client${clientsNeedingBlogs.length === 1 ? '' : 's'} ${clientsNeedingBlogs.length === 1 ? 'is' : 'are'} due for the next blog`}
          items={clientsNeedingBlogs}
          onOpenClientProfile={onOpenClientProfile}
        />
      )}
      <div className="filters-bar">
        <select className="filter-select" value={filterClient} onChange={e => setFilterClient(e.target.value)}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Phases</option>
          <option value="draft">📝 Draft</option>
          {BLOG_PHASES.map(p => <option key={p.value} value={p.value}>{p.icon} {p.label}</option>)}
        </select>
        <div style={{ marginLeft: 'auto' }}>
          {canCreate && <button className="btn btn-gold" onClick={() => setShowNew(true)}><Plus size={15} /> New Blog Assignment</button>}
        </div>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</div>
        : blogs.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: 48, marginBottom: 12 }}>✍️</div>
            <h3>No blog assignments yet</h3>
            <p>{canCreate ? 'Create a blog assignment to start the writing workflow' : 'No blogs have been assigned yet'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {blogs.map(blog => {
              const isDraft = blog.status === 'draft'
              const phase = getPhase(blog.status)
              // Build a pill from the most active task's status — this reflects exactly
              // what state the workflow is in (e.g. "awaiting client", "ready for review")
              const TASK_STATUS_PILL = {
                not_started:        { label: blog._activeTaskPhase === 'publish_checklist' ? 'Ready to Publish' : 'Not Started', icon: blog._activeTaskPhase === 'publish_checklist' ? '🚀' : '⏸', bg: blog._activeTaskPhase === 'publish_checklist' ? '#e8f5eb' : 'var(--cream-dark)', color: blog._activeTaskPhase === 'publish_checklist' ? '#2e6e3a' : 'var(--text-muted)' },
                in_progress:        { label: blog._activeTaskPhase === 'publish_checklist' ? 'Publishing in Progress' : 'Draft in Progress', icon: blog._activeTaskPhase === 'publish_checklist' ? '🚀' : '✍️', bg: '#e8f5fa', color: '#2a6e84' },
                ready_for_review:   { label: 'Ready for Review',   icon: '👁', bg: '#f9edf5',          color: '#8c4d78' },
                internally_approved:{ label: 'Internally Approved',icon: '✅', bg: '#f0fdf4',          color: '#166534' },
                awaiting_client:    { label: 'Awaiting Client',    icon: '⏳', bg: '#fff8e1',          color: '#c4962a' },
                client_approved:    { label: 'Client Approved',    icon: '✅', bg: '#e8f5eb',          color: '#2e6e3a' },
                needs_revision:     { label: 'Needs Revision',     icon: '↩', bg: '#fff8e1',          color: '#8a6000' },
                approved:           { label: 'Approved',           icon: '✅', bg: '#e8f5eb',          color: '#2e6e3a' },
                live:               { label: 'Live',               icon: '🚀', bg: '#e8f5eb',          color: '#2e6e3a' },
              }
              const activePill = blog._activeTaskStatus ? TASK_STATUS_PILL[blog._activeTaskStatus] : null
              const pillToShow = activePill || (phase ? { label: phase.label, icon: phase.icon, bg: phase.bg, color: phase.color } : null)
              const borderColor = isDraft ? 'var(--text-muted)' : (activePill?.color || phase?.color || 'var(--border)')
              return (
                <div key={blog.id} className="card" style={{ cursor: 'pointer', borderLeft: `4px solid ${borderColor}` }}
                  onClick={() => isDraft ? setEditingDraft(blog) : setSelected(blog)}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow-sm)'}>
                  <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                        {blog.working_h1 || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(untitled draft)</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                        {blog.client?.name && <span>{blog.client.name}</span>}
                        {blog.copywriter?.full_name && <span>✍️ {blog.copywriter.full_name}</span>}
                        {blog.primary_keyword && <span>🔑 {blog.primary_keyword}</span>}
                        {blog.draft_due_date && !isNaN(new Date(blog.draft_due_date)) && <span>📅 {safeFmt(blog.draft_due_date, 'MMM d')}</span>}
                      </div>
                    </div>
                    {isDraft
                      ? <span className="status-pill" style={{ background: 'var(--cream-dark)', color: 'var(--text-secondary)', flexShrink: 0 }}>📝 Draft — finish</span>
                      : pillToShow && <span className="status-pill" style={{ background: pillToShow.bg, color: pillToShow.color, flexShrink: 0 }}>{pillToShow.icon} {pillToShow.label}</span>}
                    <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  </div>
                </div>
              )
            })}
          </div>
        )
      }

      {showNew && <NewBlogModal clients={clients} profiles={profiles} currentUser={currentUser} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load() }} />}

      {editingDraft && <NewBlogModal clients={clients} profiles={profiles} currentUser={currentUser} draft={editingDraft} onClose={() => setEditingDraft(null)} onSaved={() => { setEditingDraft(null); load() }} />}

      {sidePanelTask && sidePanelBlog && (
        <BlogPhaseSidePanel
          task={sidePanelTask}
          blog={sidePanelBlog}
          allBlogTasks={sidePanelAllTasks}
          profiles={profiles}
          clients={clients}
          currentUser={currentUser}
          onSwitchTask={t => setSidePanelTask(t)}
          onClose={() => { setSidePanelTask(null); setSidePanelBlog(null); setSidePanelAllTasks([]); load() }}
          onUpdated={reloadSidePanel}
          onOpenFullWorkflow={openFullWorkflowFromPanel}
        />
      )}
    </div>
  )
}